/*
 * Keeping the screen true to the database.
 *
 * The worker writes scores into D1 every two minutes while a game is running.
 * Nothing here ever asked for them: `refresh` was only wired to `onChanged`,
 * so the app was current immediately after you did something and never again.
 * You could watch a fourth quarter with a frozen score in front of you.
 *
 * Three ways in, in the order they matter. Coming back to the app refetches,
 * which covers almost everyone almost always. A poll keeps a screen honest
 * while somebody actually sits and watches. The pull gesture is the manual
 * override on top of both - it should be how you insist, not how you cope.
 */

/** The bundle Vite emitted, e.g. `index-DgXokaIk.js`. */
const BUILD = /\/assets\/(index-[A-Za-z0-9_-]+\.js)/

/**
 * How often to refetch, or null for not at all.
 *
 * Hidden means never: a phone in a pocket through a five-hour Saturday is the
 * common case, and a background tab that keeps polling is a battery complaint
 * nobody will ever trace back to us.
 */
export function pollMs(visible: boolean, anyLive: boolean): number | null {
  if (!visible) return null
  // Half the cron's period: asking faster than the server writes only spends
  // requests, and this keeps the lag inside a single write cycle.
  if (anyLive) return 30_000
  /*
   * Slow, but not never. `locked` is decided on the server from kickoff
   * against now, so a phone left open through 12:59 goes on offering a game
   * that shut a minute ago until something asks again.
   */
  return 120_000
}

/** The running build, read off the page's own script tags. */
export function assetFromSrcs(srcs: readonly string[]): string | null {
  for (const s of srcs) {
    const m = BUILD.exec(s)
    if (m) return m[1]!
  }
  return null
}

/**
 * The build the server is handing out now.
 *
 * Matching only `.js` matters: the stylesheet is hashed the same way and comes
 * first in the document, so a looser pattern would compare a stylesheet
 * against a script and announce a new version every time it looked - which is
 * how an update prompt becomes wallpaper.
 */
export function assetFromHtml(html: string): string | null {
  const m = BUILD.exec(html)
  return m ? m[1]! : null
}

/**
 * Whether a newer build is being served than the one running.
 *
 * There is no cache to defeat - sw.js is deliberately not one - so a reload is
 * all it takes. This only decides whether to offer it, and answers false on
 * any failure: a flaky network must never nag someone to reload.
 */
export async function newBuildAvailable(): Promise<boolean> {
  try {
    const running = assetFromSrcs(Array.from(document.scripts).map((s) => s.src))
    if (!running) return false
    const html = await fetch('/', { cache: 'no-store' }).then((r) => r.text())
    const latest = assetFromHtml(html)
    return latest !== null && latest !== running
  } catch {
    return false
  }
}
